import { useFormikContext } from 'formik';
import { isObjectLike } from 'es-toolkit/compat';
import { useEffect } from 'react';

function getFirstErrorKey(errors: unknown, path: string[] = []): string[] {
  if (isObjectLike(errors)) {
    const errorObject = errors as Record<string, unknown>;
    const [key] = Object.keys(errorObject);

    if (key) {
      return getFirstErrorKey(errorObject[key], [...path, key]);
    }
  }

  if (Array.isArray(errors)) {
    return getFirstErrorKey(errors[0], [...path, '0']);
  }

  return path;
}

const getScrollableParent = (element: Element | null): Element | null => {
  if (!element) return null;
  if (element === document.documentElement) return document.documentElement;

  const style = window.getComputedStyle(element);
  const overflowY = style.getPropertyValue('overflow-y');
  const isScrollable =
    (overflowY === 'auto' || overflowY === 'scroll') &&
    element.scrollHeight > element.clientHeight;

  return isScrollable ? element : getScrollableParent(element.parentElement);
};

export function ScrollToError() {
  const formik = useFormikContext();

  useEffect(() => {
    const errors = Object.keys(formik.errors);

    if (errors.length > 0 && formik.isSubmitting) {
      const firstErrorKey = getFirstErrorKey(formik.errors);

      if (firstErrorKey.length) {
        const name = firstErrorKey.join('.');
        const errorElement = document.querySelector(
          `[name="${name}"], [aria-label="${name}"]`,
        );
        const scrollableParent = getScrollableParent(errorElement);

        if (
          errorElement &&
          scrollableParent &&
          scrollableParent !== document.documentElement
        ) {
          const elementRect = errorElement.getBoundingClientRect();
          const parentRect = scrollableParent.getBoundingClientRect();
          const top = Math.max(elementRect.top - parentRect.top, 0);

          scrollableParent.scrollTop = top; // scroll functions do not work correctly, so we use scrollTop
        } else if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  }, [formik.errors, formik.isSubmitting]);

  return null;
}
